/**
 * Settings for a group
 */

import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import produce from 'immer';
import { useApi2call } from 'containers/Api2/index.js';
import useCurrentRole from 'containers/User/useCurrentRole.js';
import {
  MODULES_MESSAGING,
  MODULES_STUDENTPORTFOLIO,
  MODULES_CONVERSATIONS,
} from 'containers/User/constants.js';
import Grid from 'components/layout/Grid/index.js';
import messages from '../messages.js';
import AboutPanel from './AboutPanel.jsx';
import SettingsPanel from './SettingsPanel.jsx';
import CommunityPermissions from './AppPermissions/CommunityPermissions.js';
import ConversationsPermissions from './AppPermissions/ConversationsPermissions.js';
import TopicPermissions from './AppPermissions/TopicPermissions.js';
import { customPermissionsReducer } from '../../../adminParser.js';
import PortfolioPanel from './PortfolioPanel.jsx';

function Settings(props) {
  const { group } = props;
  const { id, communitySettings, conversationsSettings, groupOptions, topicSettings, typeName, working } = group;
  const api2call = useApi2call();
  const reducer = (state, action) => {
    const { payload } = action;
    const newState = produce(state, (draft) => {
      draft.groupOptions = action.payload;
      draft.communitySettings.enabled = payload.community;
      draft.conversationsSettings.enabled = payload.conversations;
      draft.personalMessagesSettings.enabled = payload.personalMessages;
      draft.topicSettings.enabled = payload.topics;
    });
    return newState;
  };
  const { school } = useCurrentRole();
  const { modules } = school;

  const handleEnable = (option) => (val) => {
    const options = {
      method: 'PUT',
      version: 2,
      url: `sa/groups/${id}/options`,
      selector: `sa/groups/${typeName}/${id}`,
      body: { ...groupOptions, [option]: val },
      reducer,
    };
    api2call(options);
  };

  const handleChange = (option) => (type, body) => {
    const options = {
      method: 'PUT',
      version: 2,
      url: `sa/groups/${id}/${option}/${type}`,
      selector: `sa/groups/${typeName}/${id}/${option}/${type}`,
      body,
      reducer: type === 'customPermissions' ? customPermissionsReducer : undefined,
    };
    api2call(options);
  };

  const handleVisible = (val) => {
    const options = {
      method: 'PUT',
      version: 2,
      url: `sa/groups/${id}/portfolio/visible`,
      selector: `sa/groups/${typeName}/${id}/portfolio`,
      body: val,
    };
    api2call(options);
  };

  return (
    <Grid>
      <div className="col-narrow">
        <h3 className="text-gray my-3 mx-4 mx-md-0">
          <FormattedMessage {...messages.general} />
        </h3>
        <AboutPanel {...props} />
        <SettingsPanel {...props} onEnable={handleEnable} />
      </div>
      <div className="col-wide">
        <h3 className="text-gray my-3 mx-4 mx-md-0">
          <FormattedMessage {...messages.appsPermissions} />
        </h3>
        <CommunityPermissions
          settings={communitySettings}
          working={working}
          onChange={handleChange('communitySettings')}
        />
        {modules.includes(MODULES_MESSAGING) && !modules.includes(MODULES_CONVERSATIONS) && (
          <ConversationsPermissions
            settings={conversationsSettings}
            working={working}
            onChange={handleChange('conversationsSettings')}
            onEnable={handleEnable('conversations')}
          />
        )}
        {modules.includes(MODULES_CONVERSATIONS) && (
          <TopicPermissions
            settings={topicSettings}
            working={working}
            onChange={handleChange('topicSettings')}
            onEnable={handleEnable('topics')}
          />
        )}
        {modules.includes(MODULES_STUDENTPORTFOLIO) && (
          <PortfolioPanel
            portfolio={group.portfolio}
            working={working}
            onVisible={handleVisible}
          />
        )}
      </div>
    </Grid>
  );
}

Settings.propTypes = {
  group: PropTypes.object,
};

export default Settings;
